import { Button, Modal, Notice, Spinner } from '../ui';
import { useCopy } from '../strings/useCopy';
import { useTabImport } from './useTabImport';
import styles from './tabs.module.css';

/**
 * The confirmation the header's import control opens, mounted once for the whole app.
 *
 * It lives **inside `PlaybackHost`** rather than beside the control that opens it, because building
 * a list around the video already playing needs the session that host provides — the same reason
 * `QueueActionsHost` is there. The control is in the header, which is outside, so the press travels
 * through a module-level flag (`tabs/store.ts`) instead of through a context neither side shares.
 *
 * One dialog with three faces, in the order the reader meets them: the question, the wait, and the
 * one outcome worth a sentence. Everything closing is silent — a dialog that stayed up to say "all
 * done" would be a second press for nothing.
 */
export function TabImportHost() {
  const binding = useTabImport();
  const words = useCopy().text.tabs;

  return (
    <Modal
      open={binding.open}
      onClose={binding.close}
      title={words.ask.title}
      closeLabel={words.ask.close}
    >
      {binding.stayed ? (
        <div className={styles.content}>
          {/* `live`, because it arrives in answer to the press rather than with the dialog. */}
          <Notice tone="caution" live>
            {words.stayed}
          </Notice>
          <div className={styles.actions}>
            <Button variant="primary" onClick={binding.close}>
              {words.ask.close}
            </Button>
          </div>
        </div>
      ) : binding.busy ? (
        /* A `<div>` rather than a `<p>`: `Spinner` is a `<span>` but the sentence beside it is the
           row's second item, and this is the shape the rest of the app uses for the pair. */
        <div className={styles.waiting} role="status">
          <Spinner />
          <span>{words.ask.working}</span>
        </div>
      ) : (
        <div className={styles.content}>
          <p className={styles.detail}>{words.ask.detail}</p>
          {/* What this tab's own list is, which is what decides between the two answers below. */}
          {binding.linked ? <p className={styles.detail}>{words.ask.linked}</p> : null}
          <div className={styles.actions}>
            <Button variant="primary" onClick={binding.importNow}>
              {words.ask.confirm}
            </Button>
            {binding.linked ? (
              <Button onClick={binding.unlinkAndImport}>{words.ask.unlink}</Button>
            ) : null}
            <Button variant="ghost" onClick={binding.close}>
              {words.ask.cancel}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
